/* -----------------Globals--------------- */
import { type StyleProp, StyleSheet, type ViewStyle } from "react-native";

import { type AppTheme, useAppTheme } from "@hooks/useAppTheme";
/* -----------------UI--------------- */
import { Checkbox, List, type ListItemProps } from "react-native-paper";

/* -----------------Icons--------------- */
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useDevice } from "@hooks/useDevice";
import Box from "@memorang/ui/components/Box";
import CustomChip from "@memorang/ui/components/CustomChip";
import type { IconType } from "@memorang/ui/icon";

/* -----------------Styles--------------- */
const getStyles = (theme: AppTheme) =>
	StyleSheet.create({
		none: {
			backgroundColor: theme.colors.progress.noneBackground,
		},
		medium: {
			backgroundColor: theme.colors.progress.medium,
		},
		low: {
			backgroundColor: theme.colors.progress.low,
		},
		high: {
			backgroundColor: theme.colors.progress.high,
		},
	});

export type BgColorType = "none" | "low" | "medium" | "high";
/* -----------------Props--------------- */
type Props = {
	rightText: string;
	handleSelectCheckbox: () => void;
	bgColorType?: BgColorType;
	color?: string;
	locked?: boolean;
	status: "checked" | "unchecked";
	textColor?: string;
	disableAll?: boolean;
	testID?: string;
	icon?: IconType;
} & ListItemProps;

/* --------------Component-----------*/
const CheckboxListItem = ({
	rightText,
	title,
	handleSelectCheckbox,
	bgColorType,
	color,
	disabled,
	locked,
	status,
	textColor,
	disableAll,
	testID,
	icon,
}: Props) => {
	const theme = useAppTheme();
	const styles = getStyles(theme);

	const { isMobile } = useDevice();

	const renderLeftComp = (p: {
		color: string;
		style: StyleProp<ViewStyle>;
	}) => (
		<List.Icon
			{...p}
			icon={() => {
				return (
					<>
						{locked ? (
							<Box flexDirection="row" gap={8}>
								<Icon
									name="lock-outline"
									size={24}
									color={theme.colors.onSurfaceVariant}
								/>
								{icon && (
									<Icon name={icon} size={24} color={theme.colors.onSurface} />
								)}
							</Box>
						) : (
							<Box
								alignItems="center"
								flexDirection="row"
								gap={8}
								marginLeft={-8}
							>
								<Checkbox.Android
									aria-checked={status === "checked"}
									accessibilityLabel={
										typeof title === "string" ? title : "Checkbox item"
									}
									aria-hidden
									status={status}
									disabled={disabled || disableAll}
									testID={testID ? testID : undefined}
								/>
								{icon && (
									<Icon name={icon} size={24} color={theme.colors.onSurface} />
								)}
							</Box>
						)}
					</>
				);
			}}
		/>
	);

	const renderRightComp = (p: {
		color: string;
		style?: ViewStyle;
	}) => (
		<List.Icon
			{...p}
			icon={() => (
				<CustomChip
					style={{
						...(color
							? { backgroundColor: color }
							: bgColorType
								? styles[bgColorType]
								: null),
					}}
					textStyle={{
						color: textColor || theme.colors.white,
					}}
				>
					{rightText}
				</CustomChip>
			)}
		/>
	);
	return (
		<List.Item
			disabled={disabled || disableAll}
			onPress={handleSelectCheckbox}
			title={title}
			style={{
				marginHorizontal: isMobile ? 0 : -8,
				paddingLeft: isMobile ? 0 : 8,
				opacity: disabled || disableAll ? 0.5 : 1,
				paddingRight: isMobile ? 16 : 24,
				paddingVertical: 0,
			}}
			left={renderLeftComp}
			right={renderRightComp}
		/>
	);
};

export default CheckboxListItem;
